import AccountCircle from '@mui/icons-material/AccountCircle';
import InputAdornment from '@mui/material/InputAdornment';
import type { TextFieldVariants } from '@mui/material/TextField/TextField';
import { useAuthentication } from '@src/authentication/useAuthentication';
import { Autocomplete } from '@src/common/components/Autocomplete';
import { CircularProgress } from '@src/common/components/CircularProgress';
import { TextInput } from '@src/common/components/TextInput';
import { css } from '@src/common/styles/Styles';
import type { User } from '@src/manageUsers/domain/User';
import { useUsers } from '@src/manageUsers/hooks/useUsers';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface UserDropdownProps {
  placeholder: string;
  readOnly?: boolean;
  onUserChange: (user: User | null) => void;
  getOptionDisabled?: (user: unknown) => boolean;
  selectedUser: string | undefined;
  disabled?: boolean;
  className?: string;
  variant?: TextFieldVariants;
  disableUnderline?: boolean;
}

export const UserDropdown = ({
  placeholder,
  onUserChange,
  selectedUser,
  getOptionDisabled,
  disabled,
  readOnly,
  className,
  variant,
  disableUnderline = false,
}: UserDropdownProps) => {
  const { t } = useTranslation();
  const { userList, isLoading } = useUsers();
  const { user: currentUser } = useAuthentication();

  const reorderedUsers = useMemo(() => {
    return userList.users.slice().sort((a) => {
      if (a.email === currentUser?.email) return -1;
      return 0;
    });
  }, [userList.users, currentUser]);

  function isCurrentUserSelected(user: User) {
    return currentUser?.email === user.email;
  }

  return (
    <Autocomplete
      className={fullWidth}
      getOptionLabel={(user) =>
        isCurrentUserSelected(user as User)
          ? (user as User).name + ` (${t('me')})`
          : (user as User).name
      }
      getOptionDisabled={getOptionDisabled}
      options={reorderedUsers}
      loading={isLoading}
      value={
        selectedUser
          ? reorderedUsers.find((user) => user.id === selectedUser) || null
          : null
      }
      onChange={(_, user) =>
        onUserChange(user !== null ? (user as User) : null)
      }
      disabled={disabled}
      readOnly={readOnly}
      renderInput={(params) => (
        <TextInput
          {...params}
          className={className}
          placeholder={placeholder}
          variant={variant}
          disabled={disabled}
          readOnly={readOnly}
          disableUnderline={disableUnderline}
          slotProps={{
            input: {
              ...params.InputProps,
              readOnly,
              'aria-label': placeholder,
              endAdornment: (
                <Fragment>
                  {isLoading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </Fragment>
              ),
              startAdornment: (
                <InputAdornment position="start">
                  <AccountCircle />
                </InputAdornment>
              ),
            },
          }}
        />
      )}
    />
  );
};

const fullWidth = css({
  flex: 1,
});
